import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, X, Save, ArrowUp, ArrowDown } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';
import { Input } from './NewsManager';
import ImageUpload from './ImageUpload';
import { resolveImg } from '../../api';

const empty = { name: '', logo: '', website: '', order: 0 };

const PartnersManager = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get('/partners').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === 'new') {
        payload.order = items.length; // append at end
        await api.post('/partners', payload); toast.success('Partenaire ajouté');
      } else { await api.put(`/partners/${editing}`, payload); toast.success('Partenaire modifié'); }
      setEditing(null); setForm(empty); load();
    } catch (e) { toast.error('Erreur lors de la sauvegarde'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    await api.delete(`/partners/${id}`); toast.success('Partenaire supprimé'); load();
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    // Swap in a local copy then reindex ALL partners to 0..n-1
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    await Promise.all(next.map((p, k) => api.put(`/partners/${p.id}`, { order: k })));
    load();
  };

  const field = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div data-testid="partners-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">Nos Partenaires</h2>
        <button onClick={() => { setForm(empty); setEditing('new'); }} data-testid="add-partner-btn" className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-fuchsia-500/30 transition-all">
          <Plus size={18} /> Ajouter
        </button>
      </div>

      <p className="text-white/50 text-sm mb-6">Ajoutez le logo et le nom de chaque partenaire. Les logos apparaissent sur la page d'accueil.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item, i) => (
          <div key={item.id} className="min-w-0 flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="w-16 h-16 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
              {item.logo
                ? <img src={resolveImg(item.logo)} alt={item.name} className="w-full h-full object-contain" />
                : <span className="text-fuchsia-400 font-bold text-xl">{item.name.charAt(0)}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold truncate">{item.name}</h3>
              {item.website && <p className="text-white/40 text-xs truncate">{item.website}</p>}
            </div>
            <div className="flex flex-col gap-1 flex-shrink-0">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-white/60 hover:text-fuchsia-400 disabled:opacity-30" title="Monter" data-testid={`partner-up-${item.id}`}><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 text-white/60 hover:text-fuchsia-400 disabled:opacity-30" title="Descendre" data-testid={`partner-down-${item.id}`}><ArrowDown size={14} /></button>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => { setForm(item); setEditing(item.id); }} className="p-2 text-white/60 hover:text-fuchsia-400" data-testid={`edit-partner-${item.id}`}><Edit2 size={18} /></button>
              <button onClick={() => remove(item.id)} className="p-2 text-white/60 hover:text-red-400" data-testid={`delete-partner-${item.id}`}><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6 overflow-y-auto">
          <div className="bg-neutral-900 border border-white/10 rounded-2xl p-8 w-full max-w-lg my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">{editing === 'new' ? 'Nouveau partenaire' : 'Modifier le partenaire'}</h3>
              <button onClick={() => setEditing(null)} className="text-white/60 hover:text-white"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <Input label="Nom du partenaire" value={form.name} onChange={(v) => field('name', v)} />
              <Input label="Site web (optionnel)" value={form.website} onChange={(v) => field('website', v)} placeholder="https://..." />
              <ImageUpload value={form.logo} onChange={(v) => field('logo', v)} label="Logo du partenaire" testId="partner-logo-upload" />
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={save} data-testid="save-partner-btn" className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-lg text-white font-medium"><Save size={18} /> Enregistrer</button>
              <button onClick={() => setEditing(null)} className="px-6 py-3 bg-white/5 border border-white/10 rounded-lg text-white/70">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnersManager;
